/**
 * Chat widget for user-modern — supports text, image upload, stickers, reply, and unsend.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { chatService, type Conversation, type Message, type MessageAttachment, type MessageReplyTo } from '../services/chatService'
import { chatWebSocketService } from '../services/chatWebSocketService'
import * as authService from '../services/authService'
import { AssistiveFloatControls } from './AssistiveFloatChrome'
import { getFloatingPanelStyle, useAssistiveFloat } from '../hooks/useAssistiveFloat'
import { getChatMessagePreview, notifyAdminChatMessage } from '../lib/chatNotification'
import type { TenantThemeConfig } from '../types/theme'

interface ChatWidgetProps {
  themeConfig?: TenantThemeConfig
}

const CHAT_FLOAT_SIZE = 60
const CHAT_SYNC_OPEN_MS = 2000
const CHAT_SYNC_BACKGROUND_MS = 8000
const CHAT_SCROLL_BOTTOM_THRESHOLD = 80

function getLatestMessageTimestamp(messages: Message[]): string | null {
  if (messages.length === 0) return null
  const latest = messages.reduce((current, candidate) =>
    new Date(candidate.timestamp).getTime() > new Date(current.timestamp).getTime() ? candidate : current
  )
  return String(latest.timestamp)
}

function getIncrementalSyncSince(lastKnownAt: string | null): string | undefined {
  if (!lastKnownAt) return undefined
  return new Date(new Date(lastKnownAt).getTime() - 1000).toISOString()
}

function createOptimisticUserMessage(conversationId: string, content: string, contentType: Message['contentType'] = 'text'): Message {
  return {
    id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    conversationId,
    content,
    contentType,
    senderType: 'user',
    timestamp: new Date().toISOString(),
    read: true,
  }
}

function getConversationRoomId(conv: Conversation): string | null {
  return conv.conversationId || conv.id || null
}

function matchesConversationId(
  incomingId: string | null | undefined,
  primaryId: string | null,
  alternateId: string | null
): boolean {
  if (!incomingId) return false
  if (!primaryId && !alternateId) return true
  return incomingId === primaryId || (!!alternateId && incomingId === alternateId)
}

function getStableMessageId(message: Pick<Message, 'id'> & { messageId?: string }): string {
  return String(message.messageId ?? message.id ?? '')
}

function isSameChatMessage(a: Message, b: Message): boolean {
  const idA = getStableMessageId(a)
  const idB = getStableMessageId(b)
  if (idA && idB && idA === idB) return true
  if (a.senderType !== b.senderType || a.content !== b.content) return false
  const replyA = a.replyTo?.id ?? ''
  const replyB = b.replyTo?.id ?? ''
  if (replyA !== replyB) return false
  return Math.abs(new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) < 5000
}

function mergeMessagesById(existing: Message[], incoming: Message[]): Message[] {
  const merged = [...existing]
  for (const incomingMessage of incoming) {
    const stableId = getStableMessageId(incomingMessage)
    const normalized = stableId ? { ...incomingMessage, id: stableId } : incomingMessage
    const duplicateIndex = merged.findIndex((message) => isSameChatMessage(message, normalized))
    if (duplicateIndex >= 0) {
      if (stableId && !getStableMessageId(merged[duplicateIndex])) {
        merged[duplicateIndex] = normalized
      }
      continue
    }
    merged.push(normalized)
  }
  return merged.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )
}

function upsertIncomingMessage(prev: Message[], message: Message): Message[] {
  if (prev.some((existing) => isSameChatMessage(existing, message))) return prev
  const stableId = getStableMessageId(message)
  const normalized = stableId ? { ...message, id: stableId } : message
  const withoutOptimisticEcho = normalized.senderType === 'user'
    ? prev.filter((existing) => {
        if (!existing.id.startsWith('optimistic-')) return true
        if (existing.content !== normalized.content) return true
        const existingReplyId = existing.replyTo?.id ?? ''
        const incomingReplyId = normalized.replyTo?.id ?? ''
        return existingReplyId !== incomingReplyId
      })
    : prev
  return [...withoutOptimisticEcho, normalized]
}

/* ── Sticker Data ── */
const STICKER_CATEGORIES: Record<string, { url: string; name: string }[]> = {
  '😀 น่ารัก': [
    { url: '🥰', name: 'ยิ้ม' }, { url: '😍', name: 'รัก' }, { url: '🤗', name: 'กอด' },
    { url: '😊', name: 'หวาน' }, { url: '🥺', name: 'สงสาร' }, { url: '😘', name: 'จุ๊บ' },
    { url: '🤩', name: 'ตื่นเต้น' }, { url: '😋', name: 'อร่อย' },
  ],
  '🎉 กิจกรรม': [
    { url: '🎉', name: 'ฉลอง' }, { url: '🥳', name: 'ปาร์ตี้' }, { url: '🎈', name: 'บอลลูน' },
    { url: '🎁', name: 'ของขวัญ' }, { url: '🎂', name: 'เค้ก' }, { url: '🏆', name: 'ถ้วย' },
    { url: '🎯', name: 'เป้า' }, { url: '🎪', name: 'เซอร์คัส' },
  ],
  '❤️ ความรู้สึก': [
    { url: '❤️', name: 'รัก' }, { url: '💕', name: 'หวาน' }, { url: '💖', name: 'เต้นใจ' },
    { url: '🌹', name: 'กุหลาบ' }, { url: '💋', name: 'จุ๊บ' }, { url: '👫', name: 'คู่รัก' },
    { url: '💯', name: '100' }, { url: '🔥', name: 'ไฟ' },
  ],
  '👋 ท่าทาง': [
    { url: '👋', name: 'โบกมือ' }, { url: '👍', name: 'ชอบ' }, { url: '👎', name: 'ไม่ชอบ' },
    { url: '👏', name: 'ปรบมือ' }, { url: '🙏', name: 'ไหว้' }, { url: '✌️', name: 'ชัยชนะ' },
    { url: '🤝', name: 'จับมือ' }, { url: '💪', name: 'แข็งแรง' },
  ],
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ themeConfig }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isCompactViewport, setIsCompactViewport] = useState(false)
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [messageText, setMessageText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [showStickers, setShowStickers] = useState(false)
  const [stickerCat, setStickerCat] = useState('😀 น่ารัก')
  const [isUploading, setIsUploading] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ msgId: string; x: number; y: number } | null>(null)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const stickerRef = useRef<HTMLDivElement>(null)
  const joinedConversationIdRef = useRef<string | null>(null)
  const conversationIdRef = useRef<string | null>(null)
  const conversationAltIdRef = useRef<string | null>(null)
  const isOpenRef = useRef(false)
  const isMinimizedRef = useRef(false)
  const typingStopTimeoutRef = useRef<number | null>(null)
  const typingActiveRef = useRef(false)
  const lastKnownMessageAtRef = useRef<string | null>(null)
  const stickToBottomRef = useRef(true)

  const isAuthenticated = authService.isAuthenticated()
  const user = authService.getCurrentUser()

  /* ── Lifecycle ── */

  useEffect(() => {
    if (!isAuthenticated) return
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission()
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => setIsCompactViewport(window.innerWidth < 768)
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  const chatBottomOffset = isCompactViewport ? 88 : 24
  const chatStorageKey = `user-modern-chat-float:${themeConfig?.tenantId || 'default'}`
  const chatFloatState = useAssistiveFloat({
    storageKey: chatStorageKey,
    floatSize: CHAT_FLOAT_SIZE,
    bottomOffset: chatBottomOffset,
  })
  const chatPanelStyle = useMemo(() => {
    if (typeof window === 'undefined') {
      return { position: 'fixed' as const, left: 20, top: 80, width: 370, height: 560, zIndex: 9999 }
    }
    return getFloatingPanelStyle(chatFloatState.position, CHAT_FLOAT_SIZE)
  }, [chatFloatState.position])

  useEffect(() => {
    if (isAuthenticated) return
    chatWebSocketService.disconnect()
    joinedConversationIdRef.current = null
    conversationIdRef.current = null
    setIsOpen(false)
    setIsMinimized(false)
    setConversation(null)
    setMessages([])
    setUnreadCount(0)
    setIsLoading(false)
    setIsTyping(false)
    typingActiveRef.current = false
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current)
      typingStopTimeoutRef.current = null
    }
  }, [isAuthenticated])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current
    if (!container) return
    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      })
    })
  }, [])

  const updateStickToBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight
    stickToBottomRef.current = distance <= CHAT_SCROLL_BOTTOM_THRESHOLD
  }, [])

  useEffect(() => {
    if (!isOpen || isMinimized) return
    if (!stickToBottomRef.current) return
    scrollToBottom(messages.length > 0 ? 'smooth' : 'auto')
  }, [messages, isOpen, isMinimized, scrollToBottom])

  useEffect(() => {
    if (isOpen && !isMinimized) {
      stickToBottomRef.current = true
    }
  }, [conversation?.conversationId, conversation?.id, isOpen, isMinimized])

  useEffect(() => {
    const roomId = conversation ? getConversationRoomId(conversation) : null
    const altId =
      conversation?.conversationId && conversation?.id && conversation.conversationId !== conversation.id
        ? conversation.id
        : null
    conversationIdRef.current = roomId
    conversationAltIdRef.current = altId
  }, [conversation?.conversationId, conversation?.id, conversation])

  useEffect(() => {
    isOpenRef.current = isOpen
    isMinimizedRef.current = isMinimized
  }, [isOpen, isMinimized])

  // Close sticker picker on outside click
  useEffect(() => {
    if (!showStickers) return
    const handler = (e: MouseEvent) => {
      if (stickerRef.current && !stickerRef.current.contains(e.target as Node)) setShowStickers(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showStickers])

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return
    const handler = () => setContextMenu(null)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [contextMenu])

  /* ── WS Handlers ── */

  const handleNewMessage = useCallback((messageData: unknown) => {
    const raw = (messageData as { message?: unknown })?.message ?? messageData
    const data = raw as Record<string, unknown>
    const incomingConversationId = (data?.conversationId ?? conversationIdRef.current) as string | null
    const activeConversationId = conversationIdRef.current
    const activeAlternateId = conversationAltIdRef.current
    if (!raw || !incomingConversationId) return
    if (
      activeConversationId &&
      !matchesConversationId(incomingConversationId, activeConversationId, activeAlternateId)
    ) {
      return
    }

    const contentRaw = data.content as Record<string, unknown> | string | undefined
    let text = ''
    let contentType: Message['contentType'] = 'text'
    let attachments: Message['attachments']
    if (typeof contentRaw === 'object' && contentRaw) {
      text = ((contentRaw as { text?: string }).text ?? '') as string
      const ct = (contentRaw as { type?: string }).type
      if (ct === 'image' || ct === 'file' || ct === 'sticker') contentType = ct
      const rawAtt = (contentRaw as { attachments?: unknown[] }).attachments
      if (Array.isArray(rawAtt) && rawAtt.length) attachments = rawAtt as Message['attachments']
    } else {
      text = String(contentRaw ?? '')
    }
    if (!attachments && Array.isArray(data.attachments) && (data.attachments as unknown[]).length) {
      attachments = data.attachments as unknown as Message['attachments']
    }

    let replyTo: MessageReplyTo | undefined
    if (data.replyTo && typeof data.replyTo === 'object') {
      const r = data.replyTo as Record<string, unknown>
      replyTo = {
        id: (r.id ?? r.messageId) as string | undefined,
        content: (typeof r.content === 'string' ? r.content : (r.content as { text?: string })?.text) as string | undefined,
        senderName: (r.senderName ?? r.sender) as string | undefined,
      }
    }

    const message: Message = {
      id: (data.id ?? data.messageId ?? data._id ?? '') as string,
      conversationId: incomingConversationId,
      content: text,
      contentType,
      attachments,
      replyTo,
      senderType: ((data as any).sender?.type ?? data.senderType ?? 'user') as Message['senderType'],
      timestamp: (data.createdAt ?? data.timestamp ?? data.sentAt ?? new Date().toISOString()) as string,
      read: data.read as boolean | undefined,
    }

    setMessages((prev) => {
      const next = upsertIncomingMessage(prev, message)
      if (next === prev) return prev
      const latestAt = getLatestMessageTimestamp(next)
      if (latestAt) lastKnownMessageAtRef.current = latestAt
      return next
    })

    const chatPanelVisible = isOpenRef.current && !isMinimizedRef.current
    if (message.senderType === 'admin' && !chatPanelVisible) {
      setUnreadCount((prev) => prev + 1)
      notifyAdminChatMessage(getChatMessagePreview(message.content, message.contentType))
      return
    }

    if (chatPanelVisible && incomingConversationId) {
      void chatService.markAsRead(incomingConversationId)
      setUnreadCount(0)
    }
  }, [])

  const handleTyping = useCallback((data: { conversationId: string; isTyping: boolean }) => {
    if (matchesConversationId(data.conversationId, conversationIdRef.current, conversationAltIdRef.current)) {
      setIsTyping(data.isTyping)
    }
  }, [])
  const handleError = useCallback((err: unknown) => console.error('❌ [ChatWidget] WS error:', err), [])
  const handleRead = useCallback(() => {}, [])
  const syncMessagesFromServer = useCallback(async () => {
    const cid = joinedConversationIdRef.current || conversationIdRef.current
    if (!cid) return
    try {
      const since = getIncrementalSyncSince(lastKnownMessageAtRef.current)
      const msgs = await chatService.getMessages(cid, since ? 30 : 50, since)
      if (msgs.length === 0) return
      setMessages((prev) => {
        const merged = mergeMessagesById(prev, msgs)
        const latestAt = getLatestMessageTimestamp(merged)
        if (latestAt) lastKnownMessageAtRef.current = latestAt
        const prevIds = new Set(prev.map((message) => message.id))
        const newAdminMessages = merged.filter(
          (message) => message.senderType === 'admin' && message.id && !prevIds.has(message.id)
        )
        const chatPanelVisible = isOpenRef.current && !isMinimizedRef.current
        if (newAdminMessages.length > 0 && !chatPanelVisible) {
          const latest = newAdminMessages[newAdminMessages.length - 1]
          setUnreadCount((count) => count + newAdminMessages.length)
          notifyAdminChatMessage(getChatMessagePreview(latest.content, latest.contentType))
        }
        return merged
      })
    } catch (error) {
      console.error('❌ [ChatWidget] Failed to sync messages:', error)
    }
  }, [])
  const handleConnected = useCallback(() => {
    const cid = joinedConversationIdRef.current || conversationIdRef.current
    if (cid) {
      chatWebSocketService.joinConversation(cid)
      void syncMessagesFromServer()
    }
  }, [syncMessagesFromServer])
  const handleDisconnected = useCallback(() => {}, [])

  /* ── Chat initialization ── */

  useEffect(() => {
    if (!isAuthenticated || !user) return

    chatWebSocketService.on('message', handleNewMessage)
    chatWebSocketService.on('typing', handleTyping)
    chatWebSocketService.on('read', handleRead)
    chatWebSocketService.on('error', handleError)
    chatWebSocketService.on('connected', handleConnected)
    chatWebSocketService.on('disconnected', handleDisconnected)

    const init = async () => {
      try {
        setIsLoading(true)
        chatWebSocketService.connect()
        const c = await chatService.getOrCreateConversation(undefined)
        setConversation(c)
        const cid = getConversationRoomId(c)
        if (cid) {
          joinedConversationIdRef.current = cid
          const msgs = await chatService.getMessages(cid)
          setMessages(msgs)
          lastKnownMessageAtRef.current = getLatestMessageTimestamp(msgs)
          chatWebSocketService.joinConversation(cid)
          const adminUnread = msgs.filter((message) => message.senderType === 'admin' && message.read !== true).length
          setUnreadCount(adminUnread)
          if (isOpenRef.current && !isMinimizedRef.current) {
            await chatService.markAsRead(cid)
            setUnreadCount(0)
          }
          if (isOpenRef.current && !isMinimizedRef.current) {
            setTimeout(() => scrollToBottom('auto'), 0)
          }
        }
      } catch (error) {
        console.error('❌ [ChatWidget] Failed to init chat:', error)
      } finally {
        setIsLoading(false)
      }
    }
    void init()

    return () => {
      chatWebSocketService.off('message', handleNewMessage)
      chatWebSocketService.off('typing', handleTyping)
      chatWebSocketService.off('read', handleRead)
      chatWebSocketService.off('connected', handleConnected)
      chatWebSocketService.off('disconnected', handleDisconnected)
      chatWebSocketService.off('error', handleError)
    }
  }, [isAuthenticated, user?.id, handleConnected, handleDisconnected, handleError, handleNewMessage, handleRead, handleTyping])

  useEffect(() => {
    if (!isAuthenticated || !conversationIdRef.current) return

    const intervalMs = isOpen && !isMinimized ? CHAT_SYNC_OPEN_MS : CHAT_SYNC_BACKGROUND_MS
    const sync = () => {
      if (document.visibilityState === 'hidden') return
      void syncMessagesFromServer()
    }

    void syncMessagesFromServer()
    const syncIntervalId = window.setInterval(sync, intervalMs)
    const handleVisible = () => {
      if (document.visibilityState === 'visible') void syncMessagesFromServer()
    }
    document.addEventListener('visibilitychange', handleVisible)

    return () => {
      window.clearInterval(syncIntervalId)
      document.removeEventListener('visibilitychange', handleVisible)
    }
  }, [isAuthenticated, isOpen, isMinimized, conversation?.conversationId, conversation?.id, syncMessagesFromServer])

  /* ── Actions ── */

  const getConversationId = () => conversation?.conversationId || conversation?.id || null

  const stopTypingIndicator = useCallback(() => {
    const cid = getConversationId()
    if (!cid || !typingActiveRef.current) return
    typingActiveRef.current = false
    chatWebSocketService.sendTyping(cid, false)
  }, [conversation?.conversationId, conversation?.id])

  const queueTypingStop = useCallback(() => {
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current)
    }

    typingStopTimeoutRef.current = window.setTimeout(() => {
      stopTypingIndicator()
      typingStopTimeoutRef.current = null
    }, 1500)
  }, [stopTypingIndicator])

  const handleComposerChange = useCallback((value: string) => {
    setMessageText(value)
    const cid = getConversationId()
    if (!cid) return

    if (!value.trim()) {
      if (typingStopTimeoutRef.current) {
        window.clearTimeout(typingStopTimeoutRef.current)
        typingStopTimeoutRef.current = null
      }
      stopTypingIndicator()
      return
    }

    if (!typingActiveRef.current) {
      typingActiveRef.current = true
      chatWebSocketService.sendTyping(cid, true)
    }

    queueTypingStop()
  }, [conversation?.conversationId, conversation?.id, queueTypingStop, stopTypingIndicator])

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!messageText.trim() || !conversation || isSending) return
    const cid = getConversationId()
    if (!cid) return

    const content = messageText.trim()
    const reply = replyingTo ? { id: replyingTo.id, content: replyingTo.content, senderName: replyingTo.senderType === 'user' ? 'ฉัน' : 'แอดมิน' } : undefined
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current)
      typingStopTimeoutRef.current = null
    }
    stopTypingIndicator()
    setMessageText('')
    setReplyingTo(null)
    setIsSending(true)
    try {
      if (chatWebSocketService.isConnected()) {
        stickToBottomRef.current = true
        const optimistic = createOptimisticUserMessage(cid, content)
        if (reply) optimistic.replyTo = reply
        setMessages((prev) => [...prev, optimistic])
        chatWebSocketService.sendMessage(cid, content, { replyTo: reply })
      } else {
        const msg = await chatService.sendMessage(cid, content, { replyTo: reply })
        setMessages((prev) => [...prev, msg])
      }
    } catch {
      setMessageText(content)
    } finally {
      setIsSending(false)
    }
  }

  const handleSendSticker = async (emoji: string, name: string) => {
    if (!conversation || isSending) return
    const cid = getConversationId()
    if (!cid) return
    setShowStickers(false)
    setIsSending(true)
    try {
      if (chatWebSocketService.isConnected()) {
        const stickerContent = `${emoji} (${name})`
        const optimistic = createOptimisticUserMessage(cid, stickerContent, 'sticker')
        setMessages((prev) => [...prev, optimistic])
        chatWebSocketService.sendMessage(cid, stickerContent)
      } else {
        const msg = await chatService.sendMessage(cid, `${emoji} (${name})`, { contentType: 'sticker' })
        setMessages((prev) => [...prev, msg])
      }
    } catch { /* ignore */ } finally {
      setIsSending(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !conversation) return
    const cid = getConversationId()
    if (!cid) return
    if (file.size > 10 * 1024 * 1024) { alert('ไฟล์ใหญ่เกิน 10MB'); return }
    setIsUploading(true)
    try {
      const result = await chatService.uploadFile(file)
      if (result.success) {
        const msg = await chatService.sendMessage(cid, '', {
          contentType: file.type.startsWith('image/') ? 'image' : 'file',
          attachments: [{
            type: file.type.startsWith('image/') ? 'image' : 'file',
            mimeType: result.type,
            url: result.url,
            filename: result.filename,
            size: result.size
          }],
        })
        setMessages((prev) => [...prev, msg])
      }
    } catch { alert('อัปโหลดไม่สำเร็จ') } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDeleteMessage = async (msgId: string) => {
    const cid = getConversationId()
    if (!cid) return
    setContextMenu(null)
    try {
      await chatService.deleteMessage(cid, msgId)
      setMessages((prev) => prev.filter((m) => m.id !== msgId))
    } catch { /* ignore */ }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSendMessage() }
  }

  const handleToggle = () => {
    if (isOpen) {
      setIsMinimized(!isMinimized)
      if (isMinimized) {
        setUnreadCount(0)
        const cid = getConversationId()
        if (cid) void chatService.markAsRead(cid)
        setTimeout(() => scrollToBottom('auto'), 0)
      }
      return
    }
    setIsOpen(true)
    setIsMinimized(false)
    setUnreadCount(0)
    setTimeout(() => scrollToBottom('auto'), 0)
    const cid = getConversationId()
    if (cid) void chatService.markAsRead(cid)
  }

  const handleClose = () => {
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current)
      typingStopTimeoutRef.current = null
    }
    stopTypingIndicator()
    setIsOpen(false)
    setIsMinimized(false)
    setReplyingTo(null)
    setShowStickers(false)
    setContextMenu(null)
  }

  const handleTypingStop = useCallback(() => {
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current)
      typingStopTimeoutRef.current = null
    }
    stopTypingIndicator()
  }, [stopTypingIndicator])

  if (!isAuthenticated) return null

  const showFloatControls = !isOpen || isMinimized

  /* ── Render helpers ── */

  const renderMessage = (msg: Message) => {
    const isUser = msg.senderType === 'user'
    const isImageAttachment = (attachment?: MessageAttachment) => {
      const type = attachment?.type
      const mimeType = attachment?.mimeType
      const filename = attachment?.filename || ''
      const url = attachment?.url || ''
      return (
        type === 'image' ||
        msg.contentType === 'image' ||
        mimeType?.startsWith('image/') ||
        type?.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename) ||
        /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url)
      )
    }
    const hasImage = msg.attachments?.some((a) => isImageAttachment(a))

    return (
      <div
        key={msg.id}
        style={{ marginBottom: 10, display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', position: 'relative' }}
        onContextMenu={(e) => {
          if (!isUser) return
          e.preventDefault()
          setContextMenu({ msgId: msg.id, x: e.clientX, y: e.clientY })
        }}
      >
        <div style={{ maxWidth: '75%', position: 'relative' }}>
          {/* Reply context */}
          {msg.replyTo && (
            <div style={{
              padding: '4px 8px', marginBottom: 2, borderRadius: '8px 8px 0 0',
              backgroundColor: isUser ? '#006acc' : '#d1d5db', fontSize: 11, opacity: 0.85,
              borderLeft: '2px solid ' + (isUser ? '#fff' : '#0084ff'),
            }}>
              <div style={{ fontWeight: 'bold', color: isUser ? '#cce5ff' : '#333' }}>
                ↩ {msg.replyTo.senderName || 'ข้อความ'}
              </div>
              <div style={{ color: isUser ? '#e0f0ff' : '#555', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {msg.replyTo.content || '...'}
              </div>
            </div>
          )}

          {/* Bubble */}
          <div style={{
            padding: hasImage ? 4 : '8px 12px',
            borderRadius: msg.replyTo ? '0 0 18px 18px' : 18,
            backgroundColor: isUser ? '#0084ff' : '#e4e6eb',
            color: isUser ? '#fff' : '#000',
            wordWrap: 'break-word',
          }}>
            {/* Image attachments */}
            {msg.attachments?.map((att, i) =>
              isImageAttachment(att) ? (
                <img
                  key={i}
                  src={chatService.resolveFileUrl(att.url)}
                  alt={att.filename || 'image'}
                  style={{ maxWidth: '100%', borderRadius: 14, display: 'block', cursor: 'pointer' }}
                  onClick={() => window.open(chatService.resolveFileUrl(att.url), '_blank')}
                />
              ) : (
                <a key={i} href={chatService.resolveFileUrl(att.url)} target="_blank" rel="noreferrer"
                  style={{ color: isUser ? '#fff' : '#0084ff', textDecoration: 'underline', fontSize: 13 }}>
                  📎 {att.filename || 'ดาวน์โหลดไฟล์'}
                </a>
              ),
            )}
            {msg.content && <span style={{ fontSize: 14 }}>{msg.content}</span>}
          </div>

          {/* Action buttons (reply) */}
          <div style={{ display: 'flex', gap: 4, marginTop: 2, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
            <button type="button" onClick={() => { setReplyingTo(msg); inputRef.current?.focus() }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#888', padding: '0 4px' }}
              title="ตอบกลับ">↩</button>
            {isUser && (
              <button type="button" onClick={() => handleDeleteMessage(msg.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#c00', padding: '0 4px' }}
                title="ยกเลิกข้อความ">🗑</button>
            )}
          </div>
        </div>
      </div>
    )
  }

  /* ── Context menu ── */
  const renderContextMenu = () => {
    if (!contextMenu) return null
    return (
      <div style={{
        position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 10001,
        background: '#fff', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.2)', padding: 4, minWidth: 140,
      }}>
        <button type="button"
          onClick={() => { const m = messages.find((x) => x.id === contextMenu.msgId); if (m) { setReplyingTo(m); inputRef.current?.focus() }; setContextMenu(null) }}
          style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>
          ↩ ตอบกลับ
        </button>
        <button type="button"
          onClick={() => handleDeleteMessage(contextMenu.msgId)}
          style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#c00' }}>
          🗑 ยกเลิกข้อความ
        </button>
      </div>
    )
  }

  /* ── Main render ── */

  return (
    <>
      {renderContextMenu()}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} />

      {isOpen && !isMinimized ? (
        <div style={{
          ...chatPanelStyle,
          backgroundColor: '#fff',
          borderRadius: 18,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.06)',
          fontFamily: 'Arial, sans-serif',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #0084ff 0%, #0066cc 100%)', color: '#fff', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setIsMinimized(true)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💬</div>
                <span style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', backgroundColor: '#31d067', border: '2px solid #0072e0' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>ฝ่ายบริการลูกค้า</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>{isTyping ? 'กำลังพิมพ์...' : 'ออนไลน์ • พร้อมให้บริการ'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsMinimized(true) }} title="ย่อ"
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 6px', opacity: 0.9 }}>–</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); handleClose() }} title="ปิด"
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '0 5px', opacity: 0.9 }}>×</button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            onScroll={updateStickToBottom}
            style={{ flex: 1, overflowY: 'auto', padding: 15, backgroundColor: '#f0f2f5' }}
          >
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>กำลังโหลด...</div>
            ) : messages.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: '#666', padding: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #0084ff 0%, #0066cc 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 12, boxShadow: '0 6px 16px rgba(0,132,255,0.3)' }}>👋</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 4 }}>สวัสดีครับ ยินดีต้อนรับ</div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>มีอะไรให้เราช่วยไหมครับ?<br />พิมพ์ข้อความได้เลย เจ้าหน้าที่จะตอบกลับโดยเร็วที่สุด</div>
              </div>
            ) : messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </div>

          {/* Sticker Picker */}
          {showStickers && (
            <div ref={stickerRef} style={{ borderTop: '1px solid #e4e6eb', backgroundColor: '#fff', height: 200, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', overflowX: 'auto', padding: '6px 8px', gap: 4, borderBottom: '1px solid #eee' }}>
                {Object.keys(STICKER_CATEGORIES).map((cat) => (
                  <button key={cat} type="button" onClick={() => setStickerCat(cat)}
                    style={{
                      padding: '4px 10px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap',
                      backgroundColor: stickerCat === cat ? '#0084ff' : '#eee', color: stickerCat === cat ? '#fff' : '#333',
                    }}>{cat}</button>
                ))}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                {(STICKER_CATEGORIES[stickerCat] || []).map((s, i) => (
                  <button key={i} type="button" onClick={() => handleSendSticker(s.url, s.name)}
                    style={{
                      padding: 8, border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                      backgroundColor: 'transparent', fontSize: 24, transition: 'background .15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f2f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    title={s.name}>
                    <div>{s.url}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{s.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reply preview */}
          {replyingTo && (
            <div style={{ padding: '6px 12px', backgroundColor: '#e8f4ff', borderTop: '1px solid #cce5ff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 11, color: '#0066cc', fontWeight: 'bold' }}>↩ ตอบกลับ {replyingTo.senderType === 'user' ? 'ตัวเอง' : 'แอดมิน'}</div>
                <div style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyingTo.content || '...'}</div>
              </div>
              <button type="button" onClick={() => setReplyingTo(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>×</button>
            </div>
          )}

          {/* Input area */}
          <form onSubmit={handleSendMessage} style={{ borderTop: '1px solid #e4e6eb', padding: 10, backgroundColor: '#fff' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              {/* Image upload */}
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading}
                style={{ background: 'none', border: 'none', cursor: isUploading ? 'wait' : 'pointer', fontSize: 18, padding: '4px', color: '#666' }}
                title="แนบรูปภาพ">
                {isUploading ? '⏳' : '📷'}
              </button>

              {/* Sticker toggle */}
              <button type="button" onClick={() => setShowStickers(!showStickers)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '4px', color: showStickers ? '#0084ff' : '#666' }}
                title="สติกเกอร์">
                😀
              </button>

              <textarea
                ref={inputRef}
                value={messageText}
                onChange={(e) => handleComposerChange(e.target.value)}
                onBlur={handleTypingStop}
                onKeyDown={handleKeyPress}
                placeholder="พิมพ์ข้อความ..."
                style={{ flex: 1, border: '1px solid #e4e6eb', borderRadius: 20, padding: '8px 15px', resize: 'none', minHeight: 40, maxHeight: 100, fontFamily: 'inherit', fontSize: 14 }}
                rows={1}
              />
              <button type="submit" disabled={!messageText.trim() || isSending}
                style={{
                  backgroundColor: messageText.trim() ? '#0084ff' : '#ccc', color: '#fff', border: 'none',
                  borderRadius: '50%', width: 40, height: 40, cursor: messageText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>➤</button>
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 5, textAlign: 'center' }}>
              Enter ส่ง • Shift+Enter บรรทัดใหม่
            </div>
          </form>
        </div>
      ) : null}

      <AssistiveFloatControls
        floatState={chatFloatState}
        floatSize={CHAT_FLOAT_SIZE}
        zIndex={9999}
        onTap={handleToggle}
        ariaLabel="เปิดแชท"
        dockAriaLabel="ซ่อนปุ่มแชทไว้ขอบจอ"
        undockAriaLabel="แสดงปุ่มแชท"
        hideTitle="ซ่อนปุ่มแชท"
        hidden={!showFloatControls}
        dockedChildren={(
          <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
            <i className="fas fa-comment-dots" style={{ fontSize: 16, marginBottom: 2 }} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>Chat</span>
            {unreadCount > 0 ? (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -10,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 999,
                  background: '#f02849',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  border: '1.5px solid #fff',
                  boxSizing: 'border-box',
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </span>
        )}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1f6bff 0%, #13c6ff 100%)',
            color: '#fff',
            boxShadow: '0 6px 18px rgba(31, 107, 255, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
            <i className="fas fa-comment-dots" style={{ fontSize: 18, marginBottom: 2 }} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>Chat</span>
          </span>
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4, backgroundColor: '#f02849', color: '#fff',
              borderRadius: '50%', minWidth: 22, height: 22, padding: '0 5px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 12, fontWeight: 'bold', border: '2px solid #fff', boxSizing: 'border-box',
            }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </div>
      </AssistiveFloatControls>
    </>
  )
}

export default ChatWidget
